import type { Edge, Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../types/mindmap-components'
import { calculateEdgeStrokeWidth } from '../constants/mindmapConfig'
import { generateEdgeId } from './nodeIds'
import { calculateNodeChildrenCount } from './mindmapUtils'

/**
 * Edge utility functions for mindmap edge management
 */

/**
 * Create a new edge with hierarchy-based stroke width
 * @param sourceNodeId - ID of the source node
 * @param targetNodeId - ID of the target node
 * @param allNodes - Array of all nodes for children count calculation
 * @param allEdges - Array of all edges for children count calculation
 * @param additionalProperties - Additional edge properties to apply
 * @returns A new edge with appropriate styling
 */
export function createEdge(
  sourceNodeId: string,
  targetNodeId: string,
  allNodes: Node<ReactFlowNodeData>[],
  allEdges: Edge[] = [],
  additionalProperties: Partial<Edge> = {}
): Edge {
  const targetChildrenCount = calculateNodeChildrenCount(targetNodeId, allEdges, allNodes)
  const strokeWidth = calculateEdgeStrokeWidth(targetChildrenCount)

  return {
    id: generateEdgeId(sourceNodeId, targetNodeId),
    source: sourceNodeId,
    target: targetNodeId,
    type: 'default',
    style: {
      strokeWidth,
    },
    ...additionalProperties,
  }
}

/**
 * Style edges with hierarchy-based thickness
 * @param edges - Array of edges to style
 * @param nodes - Optional array of nodes (only used for validation, not required for styling)
 * @returns Array of styled edges
 */
export function styleEdgesWithHierarchy(edges: Edge[], nodes?: Node<ReactFlowNodeData>[]): Edge[] {
  if (edges.length === 0) return edges

  return edges.map((edge) => {
    const targetChildrenCount = calculateNodeChildrenCount(edge.target, edges, nodes)
    const strokeWidth = calculateEdgeStrokeWidth(targetChildrenCount)

    return {
      ...edge,
      style: {
        ...edge.style,
        strokeWidth,
      },
    }
  })
}
